import { EllipsedText, isValidLatitude, isValidLongitude, useMoment } from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { StyledCell, StyledRow } from 'baseui/table';
import { CSSProperties, memo } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ALARMS_ALARM_COLUMN_SIZE,
  ALARMS_COORDINATES_COLUMN_SIZE,
  ALARMS_DATE_COLUMN_MAX_WIDTH,
  ALARMS_DATE_COLUMN_SIZE,
  ALARMS_IDENT_COLUMN_SIZE,
  ALARMS_LABEL_COLUMN_SIZE,
  ALARMS_SPEED_COLUMN_SIZE,
} from '../../../constants/reports';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { assetState } from '../../../storage/assets';
import { temporaryPositionMarkerState } from '../../../storage/map';
import { selectedReportRowState } from '../../../storage/reports';
import { HistoricAlarm } from '../../../types/currentState';
import { getFormattedNumber } from '../../../utils/numbers';
import AlarmsReportEventType from './AlarmsReportEventType';

function AlarmsReportRow({
  data,
  index,
  style,
}: Readonly<
  ListChildComponentProps<{
    states: HistoricAlarm[];
    extended?: boolean;
  }>
>) {
  const state = data.states[index];
  const extended = data.extended;
  const [selected, setSelected] = useRecoilState(selectedReportRowState);
  const { theme, css, styles } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();
  const setTempPosition = useSetRecoilState(temporaryPositionMarkerState);
  const asset = useRecoilValue(assetState(state.assetId));

  const onClick = () => {
    const { gpsLat: lat, gpsLng: lng } = state;
    setSelected(index);
    if (lat && lng && isValidLatitude(lat) && isValidLongitude(lng)) {
      zoomTo({ lat, lng }, 18);
      if (extended) {
        setTempPosition({ address: '', point: { lat, lng } });
      }
    }
  };

  return (
    <StyledRow
      key={index}
      style={{ ...(style as CSSProperties), cursor: 'pointer' }}
      className={css({
        backgroundColor:
          selected === index
            ? theme.colors.gray100 // Active color
            : (index + 1) % 2 === 0
              ? theme.colors.tableStripedBackground
              : '',
        ':hover': {
          background: theme.colors.buttonTertiaryHover,
        },
      })}
      onClick={onClick}
    >
      {extended && (
        <StyledCell style={{ flex: ALARMS_IDENT_COLUMN_SIZE, ...styles.smallTableCell }}>{state.ident}</StyledCell>
      )}
      {extended && (
        <StyledCell style={{ flex: ALARMS_LABEL_COLUMN_SIZE, ...styles.smallTableCell }}>
          {asset?.label || '-'}
        </StyledCell>
      )}
      <StyledCell
        style={{ flex: ALARMS_DATE_COLUMN_SIZE, maxWidth: ALARMS_DATE_COLUMN_MAX_WIDTH, ...styles.smallTableCell }}
      >
        {formatDateFromTs(+state.gpsTime)}
      </StyledCell>
      <StyledCell
        style={{
          flex: ALARMS_ALARM_COLUMN_SIZE,
          ...styles.smallTableCell,
        }}
      >
        <AlarmsReportEventType code={state.alarmType} />
      </StyledCell>
      <StyledCell style={{ flex: ALARMS_COORDINATES_COLUMN_SIZE, ...styles.smallTableCell }}>
        <StyledLink
          href={`https://maps.google.com/?q=${state.gpsLat},${state.gpsLng}`}
          target="_blank"
          style={{ ...theme.typography.ParagraphXSmall }}
        >
          <EllipsedText
            inline
            breakAll
          >
            {`${state.gpsLat}, ${state.gpsLng}`}
          </EllipsedText>
        </StyledLink>
      </StyledCell>
      <StyledCell
        style={{
          flex: ALARMS_SPEED_COLUMN_SIZE,
          justifyContent: 'center',
          ...styles.smallTableCell,
        }}
      >
        {getFormattedNumber(state.gpsSpeed, { suffix: 'Km/h' })}
      </StyledCell>
    </StyledRow>
  );
}

export default memo(AlarmsReportRow, areEqual);
